import { authorizeUrl, exchangeCodeForToken } from '../api/console-client'
import { LoginTimeoutError } from '../errors'
import { saveAuth } from '../project/auth'
import { getServerUrl, getWorkspaceId } from '../project/config'
import { startCallbackServer } from '../server'
import { openBrowser } from '../utils/open-browser'
import { normalizeServerUrl, resolveServerTarget } from './server-target'
import type { OperationOptions } from './types'
import { emitter } from './types'

// Connecting a project to a workspace: stand up a local callback server, send the user to the console to
// authorize, trade the code it redirects back with for a deploy token, write both down.
//
// The browser is the part that makes this awkward to automate, so it is behind `openUrl`: the default hands
// the URL to the operating system, and a caller that wants to do something else — print it, drive a headless
// browser, ignore it — passes their own and reads the URL off the `authorize` event.

const DEFAULT_TIMEOUT_MS = 2 * 60 * 1000

export type LoginEvent =
  | { type: 'connecting'; serverUrl: string }
  | { type: 'authorize'; url: string }
  | { type: 'waiting' }
  | { type: 'exchanging' }
  | { type: 'connected'; serverUrl: string; workspaceId: string; workspaceName: string }

export interface LoginOptions extends OperationOptions<LoginEvent> {
  /** Overrides the server in `flowrunner.json`. A cluster name (`prod`/`dev`/`local`) or a full URL. */
  server?: string
  /** How long to wait for the browser round-trip. */
  timeoutMs?: number
  /**
   * Called with the authorization URL after it is known and before `openUrl` runs.
   *
   * The CLI waits for Enter here when the login is a step inside another command: a browser window taking
   * focus out of nowhere, mid-deploy, is startling in a way that one typed `flowrunner login` is not.
   */
  onBeforeOpen?: (url: string) => Promise<void>
  /** How the user gets the URL. Defaults to opening their browser. */
  openUrl?: (url: string) => void | Promise<void>
}

export interface LoginResult {
  serverUrl: string
  workspaceId: string
  workspaceName: string
}

export async function login(options: LoginOptions = {}): Promise<LoginResult> {
  const projectRoot = options.cwd ?? process.cwd()
  const emit = emitter(options)

  // Never empty: `getServerUrl` falls back to the environment and then to DEFAULT_SERVER_URL, so a project
  // that has never been logged in — one with no flowrunner.json at all — still has somewhere to go.
  const serverUrl = options.server
    ? resolveServerTarget(options.server)
    : normalizeServerUrl(getServerUrl(projectRoot))

  // Pre-selects the workspace the project is already attached to. The page still offers "use another
  // workspace", so `flowrunner login` is also how you move a connected project somewhere else.
  const existingWorkspaceId = getWorkspaceId(projectRoot)

  emit({ type: 'connecting', serverUrl })

  const server = await startCallbackServer()
  const callbackUrl = `http://localhost:${ server.port }/callback`

  try {
    const authUrl = authorizeUrl({ serverUrl, callbackUrl, workspaceId: existingWorkspaceId })

    emit({ type: 'authorize', url: authUrl })

    await options.onBeforeOpen?.(authUrl)
    await (options.openUrl ?? openBrowser)(authUrl)

    emit({ type: 'waiting' })

    // The timer has to be cleared, not just lost: a pending two-minute timeout keeps the event loop alive,
    // so the CLI would sit there doing nothing after a successful login — invisible while this command
    // ended in `process.exit`, and a hang once a deploy runs after it.
    let timeout: NodeJS.Timeout | undefined

    const exchangeCode = await Promise.race([
      server.waitForCode(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new LoginTimeoutError()), options.timeoutMs ?? DEFAULT_TIMEOUT_MS)
      }),
    ]).finally(() => clearTimeout(timeout))

    emit({ type: 'exchanging' })

    const { token, workspaceId, workspaceName } = await exchangeCodeForToken(serverUrl, exchangeCode)

    saveAuth({ serverUrl, workspaceId, deployToken: token, workspaceName }, projectRoot)

    emit({ type: 'connected', serverUrl, workspaceId, workspaceName: workspaceName || '' })

    return { serverUrl, workspaceId, workspaceName: workspaceName || '' }
  } finally {
    server.close()
  }
}
